/**
 * authDownload — open a backend-protected file in a new tab without losing the JWT.
 *
 * Why: `<a href="/api/...">` and `window.open("/api/...")` open a NEW browsing
 * context that cannot send the `Authorization: Bearer` header from localStorage.
 * The backend then responds 401 → user sees `{"detail":"Not authenticated"}`.
 *
 * This helper fetches the file as an authenticated blob, creates an object URL,
 * and opens THAT in a new tab. Falls back to a triggered download if popups
 * are blocked.
 */
import { api, apiError } from "./api";
import { toast } from "sonner";

const MIME_BY_EXT = {
    pdf: "application/pdf",
    md: "text/markdown",
    json: "application/json",
    csv: "text/csv",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

export async function authDownload(path, { filename, mime, openInTab = true } = {}) {
    try {
        const r = await api.get(path, { responseType: "blob" });
        const ext = (filename || path).split(".").pop().toLowerCase();
        const blob = new Blob([r.data], { type: mime || MIME_BY_EXT[ext] || r.data.type || "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        if (openInTab) {
            const win = window.open(url, "_blank", "noopener,noreferrer");
            if (!win) {
                const a = document.createElement("a");
                a.href = url; a.download = filename || "download";
                document.body.appendChild(a); a.click(); a.remove();
            }
        } else {
            const a = document.createElement("a");
            a.href = url; a.download = filename || "download";
            document.body.appendChild(a); a.click(); a.remove();
        }
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
        return true;
    } catch (e) {
        toast.error(apiError(e));
        return false;
    }
}
